import { useState, useMemo, useCallback, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RpFiltersModal from 'components/commons/RpFiltersModal';
import { CHECKBOX_FILTERS } from 'constants/filterOptions';
import SearchableCheckboxSelect from 'components/FilterFields/SearchableCheckboxSelect';
import useFilterStore from 'zustandStore/useFilterStore';
import {
    COMMERCIAL_FILTERS,
    CORE_FILTERS, DISTANCE_FILTERS, EXTERNAL_FILTERS, FRONTAGE_FILTERS, INTERNAL_FILTERS,
    NAVIGATION_FILTERS_AU,
    NAVIGATION_FILTERS_NZ, PROPERTY_VALUES_FILTERS, RURAL_FILTERS, SERVICE_FILTERS, STRUCTURE_FILTERS
} from 'constants/advancedFilters';
import { isNZLocale } from 'lib/PropertyUtils';
import { UPDATE } from 'ducks/filters';
import RpFiltersModalActionButtons from 'components/commons/RpFiltersModalActionButtons';
import useFetchMetadata from 'hooks/useFetchMetadata';
import { UNENTITLED_VALIDATION_MESSAGE } from 'constants/validationMessages';

import { SALES, RENTALS, LISTINGS } from 'constants/comparableTypes';
import { trackRefineSearch, trackResetFiltersEvent } from 'lib/MixpanelUtil';

const COMPARABLE_STATE = {
    [LISTINGS]: 'comparableListings',
    [SALES]: 'comparableSales',
    [RENTALS]: 'comparableRentals',
};

const getDefaultTabValue = () => {
    const filters = isNZLocale() ? NAVIGATION_FILTERS_NZ : NAVIGATION_FILTERS_AU;
    return filters[0].value;
};

const getNavigationFilters = (isEntitledForAdvancedFilters, hasAdvancedFilterRole) => {
    const advancedFilters = isNZLocale() ? NAVIGATION_FILTERS_NZ : NAVIGATION_FILTERS_AU;

    if (!isEntitledForAdvancedFilters) {
        return [advancedFilters[0]];
    }

    if (!hasAdvancedFilterRole) {
        return advancedFilters.map(tab =>
            tab.advancedFilter
                ? { ...tab, disabled: true, component: null }
                : tab
        );
    }

    return advancedFilters;
};

function AdvancedFilterModal(props) {
    const dispatch = useDispatch();
    const {
        comparableState,
        propertyId,
    } = useSelector(state => ({
        propertyId: state.propertyDetail.propertyId,
        comparableState: state[COMPARABLE_STATE[props.comparableType]],
    }));

    const [tabIndex, setTabIndex] = useState(getDefaultTabValue());
    const [activeSlot, setActiveSlot] = useState(null);

    const {
        propertySubTypeOptions,
        landUseOptions,
        devZoneOptions,
        getNonDefaultFilterCount,
        isAppliedFiltersDirty,
        resetFilters,
        hasAppliedFilterChanged,
        isFiltersDirty,
        filters,
        applyFilters,
        hasAdvancedFilterRole,
        isEntitledForAdvancedFilters,
        errors,
    } = useFilterStore(state => state);

    const coreFiltersCount = getNonDefaultFilterCount(props.comparableType, CORE_FILTERS);
    const commercialFiltersCount = getNonDefaultFilterCount(props.comparableType, COMMERCIAL_FILTERS);
    const distanceFiltersCount = getNonDefaultFilterCount(props.comparableType, DISTANCE_FILTERS);
    const externalFiltersCount = getNonDefaultFilterCount(props.comparableType, EXTERNAL_FILTERS);
    const frontageFiltersCount = getNonDefaultFilterCount(props.comparableType, FRONTAGE_FILTERS);
    const internalFiltersCount = getNonDefaultFilterCount(props.comparableType, INTERNAL_FILTERS);
    const propertyValuesFiltersCount = getNonDefaultFilterCount(props.comparableType, PROPERTY_VALUES_FILTERS);
    const ruralFiltersCount = getNonDefaultFilterCount(props.comparableType, RURAL_FILTERS);
    const serviceFiltersCount = getNonDefaultFilterCount(props.comparableType, SERVICE_FILTERS);
    const structureFiltersCount = getNonDefaultFilterCount(props.comparableType, STRUCTURE_FILTERS);
    const disabledApplyFilter = !hasAppliedFilterChanged(props.comparableType);
    const disabledResetFilter = !isFiltersDirty(props.comparableType);

    const filtersCategories = useMemo(() => ({
        core_filters: coreFiltersCount,
        commercial: commercialFiltersCount,
        distances: distanceFiltersCount,
        external: externalFiltersCount,
        frontage: frontageFiltersCount,
        internal: internalFiltersCount,
        property_values: propertyValuesFiltersCount,
        rural: ruralFiltersCount,
        service: serviceFiltersCount,
        structure: structureFiltersCount,
    }), [
        coreFiltersCount,
        commercialFiltersCount,
        distanceFiltersCount,
        externalFiltersCount,
        frontageFiltersCount,
        internalFiltersCount,
        propertyValuesFiltersCount,
        ruralFiltersCount,
        serviceFiltersCount,
        structureFiltersCount
    ]);

    // This effect fetches the total number of matching properties whenever filters change
    // or when the modal is opened
    const {
        totalElements = '',
        fetching,
    } = useFetchMetadata(props.open, props.comparableType, filters, errors[props.comparableType]?.length);

    const handleConfirmReset = useCallback(() => {
        if (isAppliedFiltersDirty(props.comparableType)) {
            dispatch({ type: UPDATE, payload: {} });
            resetFilters(props.comparableType);
            props.applyFilter();
            trackResetFiltersEvent(props.comparableType, propertyId);
        } else {
            resetFilters(props.comparableType);
            trackResetFiltersEvent(props.comparableType, propertyId);
        }
    }, [dispatch, isAppliedFiltersDirty, props.comparableType, props.applyFilter, resetFilters]);

    const handleModalClose = useCallback(() => {
        props.handleAdvancedFilterModal(false);
        setActiveSlot(null);
        setTabIndex(getDefaultTabValue());
    }, [props.handleAdvancedFilterModal]);

    const tabs = useMemo(() => getNavigationFilters(isEntitledForAdvancedFilters, hasAdvancedFilterRole).map(tab => ({
        ...tab,
        contentCount: filtersCategories[tab.value] || 0,
    })), [filtersCategories, isEntitledForAdvancedFilters, hasAdvancedFilterRole]);

    // This controls what to render in the slide panel
    // Used in property sub type, land use, dev zone filters
    const getSlidePanelSlot = () => {
        if (!activeSlot) return;
        const options = {
            [CHECKBOX_FILTERS.propertySubType.key]: propertySubTypeOptions,
            [CHECKBOX_FILTERS.landUse.key]: landUseOptions,
            [CHECKBOX_FILTERS.devZone.key]: devZoneOptions,
        };

        return (<SearchableCheckboxSelect
            label={CHECKBOX_FILTERS[activeSlot].label}
            attr={activeSlot}
            comparableType={props.comparableType}
            isDropdown={false}
            options={options[activeSlot]}
            fullView
        />);
    };

    const onApplyFilters = useCallback(() => {
        // this is a workaround to force re-fetching data and make filters dirty
        // see line 241 of src/actionCreators/comparableItems.js
        dispatch({ type: UPDATE, payload: {} });
        applyFilters(props.comparableType);
        props.applyFilter();
        handleModalClose();
        trackRefineSearch(props.comparableType, propertyId, filters);
    }, [applyFilters, props.comparableType, handleModalClose, filters], propertyId);

    const metadataResultText = totalElements === '0' ? 'No matching properties found' :
        `Show ${totalElements} matching ${Number(`${totalElements}`.replace(/\D/g, '')) > 1 ? 'properties' : 'property'}`;

    return (
        <RpFiltersModal
            open={props.open}
            onClose={handleModalClose}
            tabIndex={tabIndex}
            onTabChange={setTabIndex}
            tabs={tabs}
            slidePanelSlot={getSlidePanelSlot()}
            onSlidePanelClose={() => setActiveSlot(null)}
            featureLocked={UNENTITLED_VALIDATION_MESSAGE}
            actionButtons={
                <RpFiltersModalActionButtons
                    applyFilters={onApplyFilters}
                    disabledApplyFilters={disabledApplyFilter || fetching || errors[props.comparableType]?.length}
                    onReset={handleConfirmReset}
                    disabledClearFilters={disabledResetFilter}
                    applyText={
                        <>
                            {metadataResultText}
                            <ArrowForwardIcon sx={{ fontSize: 20, marginLeft: 1.5 }} />
                        </>
                    }
                    loading={fetching || comparableState?.pagination?.totalElements === undefined}
                />
            }
        >
            {tabs.map((tab) => {
                const TabComponent = tab.component;
                return TabComponent && (
                    <TabComponent
                        key={tab.value}
                        comparableType={props.comparableType}
                        setActiveSlot={setActiveSlot}
                    />
                );
            })}
        </RpFiltersModal>
    );
}

AdvancedFilterModal.propTypes = {
    comparableType: PropTypes.oneOf([SALES, RENTALS, LISTINGS]).isRequired,
    open: PropTypes.bool.isRequired,
    handleAdvancedFilterModal: PropTypes.func.isRequired,
    applyFilter: PropTypes.func.isRequired,
};

export default memo(AdvancedFilterModal);
